import { useId } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { IconButton } from '../actions/IconButton';
import { Select } from '../forms/Select';
import { formatCount } from '../../lib/format/money';

/**
 * Page controls under a DataTable, driven by the normalised `Page<T>` meta
 * (both BE paging shapes map to it — lib/api/paging). "Showing 26–50 of 51".
 */
export function Pager({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizes = [25, 50, 100],
  noun = 'rows',
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizes?: number[];
  noun?: string;
}) {
  const sizeId = useId();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  return (
    <nav
      aria-label="Pagination"
      className="mt-3 flex flex-wrap items-center justify-between gap-3 font-body text-[13px] text-muted"
    >
      <span aria-live="polite">
        Showing {formatCount(from)}–{formatCount(to)} of {formatCount(total)} {noun}
      </span>
      <div className="flex items-center gap-2">
        {onPageSizeChange && (
          <span className="flex items-center gap-2">
            <label htmlFor={sizeId}>Per page</label>
            <span className="w-[84px]">
              <Select
                id={sizeId}
                compact
                surface="white"
                value={String(pageSize)}
                options={pageSizes.map(String)}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
              />
            </span>
          </span>
        )}
        <IconButton ariaLabel="Previous page" size={32} disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          <ChevronLeft size={16} aria-hidden="true" />
        </IconButton>
        <span className="tabular-nums text-ink">
          {page} / {totalPages}
        </span>
        <IconButton
          ariaLabel="Next page"
          size={32}
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight size={16} aria-hidden="true" />
        </IconButton>
      </div>
    </nav>
  );
}
